import {HttpErrorResponse} from "@angular/common/http";
import {Injectable} from "@angular/core";

import {ValBaseService} from "../ValBaseService.service";
import {ValApiResultat} from "../ValApiResultat";
import {ValAppSettingsService} from "../../../../val-app-settings-lib/src/public-api";
import {ValAbonnementNotification, ValAbonnementsUtilisateur, ValCanalNotification, ValTesterCanalNotificationResultat} from "./ValAbonnementNotification";

//Service d’accès aux abonnements de l’utilisateur courant (API Ammon Notification, routes v1.0/abonnement).
//L’utilisateur et le client sont lus par l’API dans le jeton : ils ne transitent jamais par le corps
@Injectable (
{
  providedIn: 'root'
})
export class ValAbonnementsNotificationService extends ValBaseService
{
  //Création
  //  Paramètres
  //    appSettingsService : Service d’accès à la configuration de l’application
  constructor (private appSettingsService: ValAppSettingsService)
  {
    super ();
  }

  //Url de base
  protected override get BaseUrl () : string
  {
    return `${this.UrlApi}/v1.0/abonnement`;
  }

  //Url de l’API lue dans la configuration, sans « / » final
  private get UrlApi () : string
  {
    return (this.appSettingsService.Configuration?.UrlApi?.Notification ?? "").trim ().replace (/\/+$/, "");
  }

  //Indique si l’API est configurée. Une URL vide, ou une variable que le conteneur n’a pas
  //substituée (${URL_API_NOTIFICATION}), ne l’est pas
  public get Configuree () : boolean
  {
    return /^https?:\/\/[^$]+$/i.test (this.UrlApi);
  }

  //Retourne les types d’événement autorisés, les abonnements et l’adresse e-mail de l’utilisateur courant
  //  Retourne
  //    Les abonnements de l’utilisateur. Lève une erreur si l’API n’est pas configurée ou refuse
  public async RetournerAbonnements () : Promise<ValAbonnementsUtilisateur>
  {
    let abonnements: ValAbonnementsUtilisateur;

    this.VerifierConfiguration ();

    abonnements = this.Extraire (await this.ExecuterRequeteGet<ValAbonnementsUtilisateur> (""));

    return {
      adresseEmail: abonnements?.adresseEmail ?? null,
      typesEvenement: Array.isArray (abonnements?.typesEvenement) ? abonnements.typesEvenement : [],
      abonnements: Array.isArray (abonnements?.abonnements) ? abonnements.abonnements : []
    };
  }

  //Abonne l’utilisateur courant à un type d’événement sur un canal.
  //Un abonnement identique (même type, même canal) remplace le précédent : pour Teams, il remplace l’URL
  //  Paramètres
  //    codeTypeEvenement : Code du type d’événement
  //    canal             : Canal
  //    urlTeams          : URL du workflow Teams, obligatoire pour TEAMS, ignorée pour EMAIL
  //  Retourne
  //    L’abonnement enregistré
  public async Abonner (codeTypeEvenement: string, canal: ValCanalNotification, urlTeams: string | null) : Promise<ValAbonnementNotification>
  {
    this.VerifierConfiguration ();

    return this.Extraire (await this.ExecuterRequetePost<ValAbonnementNotification> ("", {codeTypeEvenement: codeTypeEvenement, canal: canal, urlTeams: urlTeams}));
  }

  //Supprime un abonnement de l’utilisateur courant
  //  Paramètres
  //    id : Identifiant de l’abonnement
  public async Desabonner (id: string) : Promise<void>
  {
    let donnees: ValApiResultat<unknown>;

    this.VerifierConfiguration ();

    donnees = await this.ExecuterRequeteDelete<unknown> (`/${encodeURIComponent (id)}`, null);

    //Réponse vide ou enveloppe en succès
    if (this.EstEnveloppe (donnees))
      this.Extraire (donnees);
  }

  //Envoie un message de test sur un canal.
  //Pour Teams sans URL, l’API utilise l’URL déjà enregistrée de l’utilisateur
  //  Paramètres
  //    canal    : Canal
  //    urlTeams : URL du workflow Teams à tester, null pour l’URL enregistrée
  //  Retourne
  //    Le résultat de l’envoi
  public async TesterCanal (canal: ValCanalNotification, urlTeams: string | null) : Promise<ValTesterCanalNotificationResultat>
  {
    this.VerifierConfiguration ();

    return this.Extraire (await this.ExecuterRequetePost<ValTesterCanalNotificationResultat> ("/test", {canal: canal, urlTeams: urlTeams}));
  }

  //Retourne le message d’erreur porté par une réponse de l’API (enveloppe ValApiResultat d’un 400…)
  //  Paramètres
  //    erreur : Erreur levée par un appel
  //  Retourne
  //    Le message, null si l’erreur n’en porte pas d’exploitable
  public MessageErreur (erreur: unknown) : string | null
  {
    let corps: unknown;
    let message: string | null;

    corps = erreur instanceof HttpErrorResponse ? erreur.error : null;
    message = null;

    if (this.EstEnveloppe (corps))
      message = (corps as ValApiResultat<unknown>).message || ((corps as ValApiResultat<unknown>).messages ?? []).join (" ");
    else if (!(erreur instanceof HttpErrorResponse) && erreur instanceof Error)
      message = erreur.message;

    return message?.trim () || null;
  }

  //Lève une erreur si l’API n’est pas configurée
  private VerifierConfiguration () : void
  {
    if (!this.Configuree)
      throw new Error ("");
  }

  //Retourne le résultat d’une enveloppe en succès, lève une erreur portant son message sinon
  //  Paramètres
  //    donnees : Réponse de l’API
  private Extraire<T> (donnees: ValApiResultat<T>) : T
  {
    if (!this.EstEnveloppe (donnees) || donnees.status !== "success")
      throw new Error (donnees?.message || (donnees?.messages ?? []).join (" "));

    return donnees.resultat as T;
  }

  //Indique si la réponse est une enveloppe ValApiResultat
  //  Paramètres
  //    donnees : Réponse de l’API
  private EstEnveloppe (donnees: unknown) : boolean
  {
    return !!donnees && typeof donnees === "object" && !Array.isArray (donnees) && "status" in donnees;
  }
}
